// Layered honeycomb backdrop: a tiled hexagon grid masked to fade out, a warm
// radial light, and a few floating hexagon particles for depth. Pointer
// parallax is driven by `--rf-px` / `--rf-py` set on an ancestor (see Raffle
// hero) so motion costs nothing in React render terms. Reads as a premium
// brand backdrop — geometric and warm, never a casino.

import React, { useMemo } from 'react';

// Deterministic-ish particle layout so SSR/first paint is stable per mount.
const buildParticles = (count) =>
  Array.from({ length: count }).map((_, i) => ({
    id: i,
    left: 6 + ((i * 37) % 88),
    top: 8 + ((i * 53) % 78),
    size: 26 + ((i * 17) % 46),
    depth: 0.4 + ((i % 5) * 0.32), // parallax depth factor
    delay: (i % 6) * 0.9,
    duration: 9 + (i % 5) * 2.5,
    spin: i % 2 === 0 ? 1 : -1,
  }));

const HexParticle = ({ p }) => (
  <span
    className="rf-hex-particle"
    style={{
      left: `${p.left}%`,
      top: `${p.top}%`,
      '--rf-hp-size': `${p.size}px`,
      '--rf-hp-depth': p.depth,
      '--rf-hp-delay': `${p.delay}s`,
      '--rf-hp-dur': `${p.duration}s`,
      '--rf-hp-spin': p.spin,
    }}
    aria-hidden="true"
  >
    <svg viewBox="0 0 28 32" width="100%" height="100%" fill="none">
      <path
        d="M14 1.2 26 8v16l-12 6.8L2 24V8z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const HoneycombBackground = ({ particles = 7, withParticles = true }) => {
  const dots = useMemo(
    () => (withParticles ? buildParticles(particles) : []),
    [particles, withParticles]
  );

  return (
    <div className="rf-honeycomb" aria-hidden="true">
      <span className="rf-honeycomb__glow rf-honeycomb__glow--primary" />
      <span className="rf-honeycomb__glow rf-honeycomb__glow--secondary" />

      <svg className="rf-honeycomb__grid rf-honeycomb__grid--back" width="100%" height="100%">
        <defs>
          <pattern
            id="rf-hex-back"
            width="56"
            height="48"
            patternUnits="userSpaceOnUse"
            patternTransform="scale(1.9)"
          >
            <path
              d="M14 1 L42 1 L56 24 L42 47 L14 47 L0 24 Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#rf-hex-back)" />
      </svg>

      <svg className="rf-honeycomb__grid rf-honeycomb__grid--front" width="100%" height="100%">
        <defs>
          <pattern
            id="rf-hex-front"
            width="56"
            height="48"
            patternUnits="userSpaceOnUse"
            patternTransform="scale(1.1)"
          >
            <path
              d="M14 1 L42 1 L56 24 L42 47 L14 47 L0 24 Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#rf-hex-front)" />
      </svg>

      {withParticles && (
        <div className="rf-honeycomb__particles">
          {dots.map((p) => (
            <HexParticle key={p.id} p={p} />
          ))}
        </div>
      )}
    </div>
  );
};

export default HoneycombBackground;
